import React, { useState, useEffect, useCallback } from 'react';
import { Button, Box } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect, {
  DropdownSelectOption
} from '../../../components/DropdownSelect/DropdownSelect';
import TopLabelTextField from '../../../components/TopLabelTextField/TopLabelTextField';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import {
  StudentPaymentSchemeWithMetrics,
  FeeFineDetailsData
} from '../../../types/paymentScheme';
import { bankOptions } from '../../../constants/banks';
import _ from 'lodash';
import { TransformedStudentDetail } from '../../../types/userType';
import paymentsApis, {
  SavePaymentPayload,
  FinePayload,
  ChequeDetailsResponse
} from '../../../apis/payments/payments';
import { PaymentMode, TransactionStatus } from '../../../enums';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomDatePicker from '../../../components/DatePicker/CustomDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import SelectAll from '../../../components/SelectAllField/SelectAllField';

interface AddPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  selectedFeeDetails: StudentPaymentSchemeWithMetrics[];
  selectedFeeFineDetails: FeeFineDetailsData[];
  userDetails: TransformedStudentDetail | null;
}

const AddPaymentDialog: React.FC<AddPaymentDialogProps> = ({
  open,
  onClose,
  onSave,
  selectedFeeDetails,
  selectedFeeFineDetails,
  userDetails
}) => {
  const [particulars, setParticulars] = useState<string[]>([]);
  const [amountPayable, setAmountPayable] = useState(0);
  const [amountPaid, setAmountPaid] = useState<number | null>(null);
  const [paymentMode, setPaymentMode] = useState<PaymentMode | ''>('');
  const [paymentDate, setPaymentDate] = useState<Dayjs | null>(dayjs());
  const [depositUptoDate, setDepositUptoDate] = useState<Dayjs | null>(null);
  const [remarks, setRemarks] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<TransactionStatus>(
    TransactionStatus.NOT_DEPOSITED
  );
  const [chequeNumber, setChequeNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [branchName, setBranchName] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [loading, setLoading] = useState(false);

  const resetState = useCallback(() => {
    setParticulars([]);
    setAmountPayable(0);
    setAmountPaid(null);
    setPaymentMode('');
    setPaymentDate(dayjs());
    setDepositUptoDate(null);
    setRemarks('');
    setPaymentStatus(TransactionStatus.NOT_DEPOSITED);
    setChequeNumber('');
    setBankName('');
    setBranchName('');
    setTransactionId('');
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    let totalAmountPayable = 0;
    const combinedParticulars: string[] = [];

    selectedFeeDetails.forEach((fee) => {
      let amountToBePaid = 0;
      if (fee.paidAmount) {
        amountToBePaid =
          fee.amount - fee.paidAmount > 0 ? fee.amount - fee.paidAmount : 0;
      } else {
        amountToBePaid = fee.amount;
      }
      totalAmountPayable += amountToBePaid;
      combinedParticulars.push(fee.particulars);
    });

    if (selectedFeeFineDetails.length > 0) {
      const lateFeeAmount = selectedFeeFineDetails.reduce(
        (sum, paymentScheme) =>
          sum +
          (paymentScheme.lateFee - paymentScheme.paidLateFee) +
          (paymentScheme.chequeBounceFee - paymentScheme.paidChequeBounceFee),
        0
      );
      totalAmountPayable += lateFeeAmount;
      combinedParticulars.push('Late Fee / Cheque Bounce Fee');
    }

    setParticulars(combinedParticulars);
    setAmountPayable(totalAmountPayable);
  }, [open, resetState, selectedFeeDetails, selectedFeeFineDetails]);

  const isSaveDisabled =
    particulars.length === 0 ||
    amountPaid === null ||
    !paymentMode ||
    !paymentDate ||
    !depositUptoDate ||
    !paymentStatus ||
    ((paymentMode === PaymentMode.CHEQUE ||
      paymentMode === PaymentMode.DEMAND_DRAFT) &&
      (!chequeNumber || !bankName || !branchName));

  const handleSave = async () => {
    setLoading(true);
    const modeOfPayment = paymentMode;
    let totalAmountPaid = amountPaid === null ? 0 : amountPaid;
    const transactionIdInput = transactionId;

    let chequeDetailsResponse: ChequeDetailsResponse | undefined;

    if (
      paymentMode === PaymentMode.CHEQUE ||
      paymentMode === PaymentMode.DEMAND_DRAFT
    ) {
      const chequeDetailsPayload = {
        chequeNo: chequeNumber,
        bank: bankName,
        branch: branchName,
        issueDate: paymentDate?.format('YYYY-MM-DD') || ''
      };
      try {
        chequeDetailsResponse = await paymentsApis.saveChequeDetails(
          chequeDetailsPayload
        );
      } catch (error) {
        console.error('Error saving cheque details:', error);
        setLoading(false);
        return;
      }
    }

    const getTransactionId = () => {
      if (
        paymentMode === PaymentMode.CHEQUE ||
        paymentMode === PaymentMode.DEMAND_DRAFT
      ) {
        return chequeDetailsResponse?.id || '';
      }
      const newGeneratedTransactionId =
        'Transaction-' + modeOfPayment + '-' + new Date().getTime();
      return _.isEmpty(transactionIdInput)
        ? newGeneratedTransactionId
        : transactionIdInput;
    };

    const generateReceiptNo = () => {
      const userSchool = userDetails?.studentDetails?.school || '';
      const timestamp = new Date().getTime();
      const studentEnrollmentNo = userDetails?.studentDetails?.enrollmentNo;

      return !_.isEmpty(studentEnrollmentNo)
        ? `${userSchool}/${studentEnrollmentNo}/${timestamp}`
        : `${userSchool}/${timestamp}`;
    };

    const transactionIdValue = getTransactionId();
    const receiptNo = generateReceiptNo();

    const paymentSchemesData: Array<{
      id: string;
      amount: number;
      paidAmount: number;
    }> = [];

    const sortedSelectedFeeDetails = [...selectedFeeDetails].sort(
      (d1, d2) =>
        dayjs(d1.paymentDueDate).toDate().getTime() -
        dayjs(d2.paymentDueDate).toDate().getTime()
    );

    sortedSelectedFeeDetails.forEach((item) => {
      paymentSchemesData.push({
        id: item._id,
        amount: item.amount,
        paidAmount: item.paidAmount
      });
    });

    const finesData: FinePayload[] = selectedFeeFineDetails.map((fine) => ({
      _id: fine._id,
      particulars: fine.particulars,
      feePeriod: fine.feePeriod,
      academicYear: fine.academicYear,
      chequeBounceFee: fine.chequeBounceFee,
      lateFee: fine.lateFee,
      paidLateFee: fine.paidLateFee,
      pendingLateFee: fine.pendingLateFee,
      paidChequeBounceFee: fine.paidChequeBounceFee,
      pendingChequeBounceFee: fine.pendingChequeBounceFee,
      dueDays: fine.dueDays,
      dueMonths: fine.dueMonths,
      additionalConcessionsAndWaivers: (
        fine.additionalConcessionsAndWaivers || []
      ).map((concession) => ({
        description: concession.description,
        amount: concession.amount,
        status: concession.status,
        type: concession.type,
        appliedOn: concession.appliedOn,
        _id: (concession as any)._id || concession.id
      })),
      paymentSchemeId: fine._id
    }));

    const selectedDate = paymentDate;
    const isCurrentDate = dayjs().isSame(selectedDate, 'day');
    const finalPaymentDate = isCurrentDate
      ? dayjs().format('YYYY-MM-DD')
      : selectedDate?.format('YYYY-MM-DD') || '';

    const paymentData: SavePaymentPayload = {
      paymentDate: finalPaymentDate,
      transactionId: transactionIdValue,
      status: TransactionStatus.NOT_DEPOSITED,
      modeOfPayment,
      paymentSchemes: paymentSchemesData,
      amount: totalAmountPaid,
      remark: remarks,
      feeDepositedUpto: depositUptoDate?.format('YYYY-MM-DD') || '',
      receiptNo,
      userId: userDetails?.studentDetails?.id || '',
      fines: finesData
    };

    try {
      await paymentsApis.savePayment(paymentData);
      onSave();
      onClose();
      resetState();
    } catch (error) {
      console.error('Error saving payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const paymentModesOptions: DropdownSelectOption[] = Object.values(
    PaymentMode
  ).map((mode) => ({
    value: mode,
    label: mode
  }));

  const particularOptions: DropdownSelectOption[] = particulars.map((p) => ({
    value: p,
    label: p
  }));

  const paymentStatusOptions: DropdownSelectOption[] = [
    { value: TransactionStatus.NOT_DEPOSITED, label: 'Not Deposited' }
  ];

  const dialogActions = (
    <Button
      variant='contained'
      onClick={handleSave}
      sx={{ textTransform: 'none' }}
      disabled={isSaveDisabled}
    >
      Save
    </Button>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Add Payment'
      maxWidth='md'
      actions={dialogActions}
    >
      <OverlayLoader show={loading} />
      <Box sx={{ mb: 2 }}>
        <SelectAll
          label='Particulars'
          placeholder='Select Particulars'
          options={particularOptions}
          selected={particulars}
          onChange={setParticulars}
          hasTopLabel
          disabled
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <TopLabelTextField
            label='Amount Payable'
            type='number'
            value={amountPayable}
            onChange={(e) => setAmountPayable(Number(e.target.value))}
            fullWidth
            hasTopLabel
            disabled
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TopLabelTextField
            label='Amount Paid'
            type='number'
            value={amountPaid === null ? '' : amountPaid}
            onChange={(e) =>
              setAmountPaid(
                e.target.value === '' ? null : Number(e.target.value)
              )
            }
            required
            fullWidth
            hasTopLabel
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DropdownSelect
            label='Payment Mode'
            value={paymentMode}
            options={paymentModesOptions}
            onChange={(e) => setPaymentMode(e.target.value as PaymentMode)}
            name='paymentMode'
            fullWidth
            hasTopLabel
            required
          />
        </Box>
      </Box>
      {(paymentMode === PaymentMode.CHEQUE ||
        paymentMode === PaymentMode.DEMAND_DRAFT) && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <TextInputWithIcon
                label='Cheque Number'
                value={chequeNumber}
                onChange={(e) => setChequeNumber(e.target.value)}
                fullWidth
                hasTopLabel
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <DropdownSelect
                label='Bank Name'
                value={bankName}
                options={bankOptions}
                onChange={(e) => setBankName(e.target.value as string)}
                name='bankName'
                fullWidth
                hasTopLabel
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextInputWithIcon
                label='Branch Name'
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                fullWidth
                hasTopLabel
              />
            </Box>
          </Box>
        </>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {!_.includes(
          [PaymentMode.CHEQUE, PaymentMode.DEMAND_DRAFT, PaymentMode.CASH],
          paymentMode
        ) && !_.isEmpty(paymentMode) ? (
          <Box sx={{ flex: 1 }}>
            <TopLabelTextField
              label='Transaction ID'
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              fullWidth
              hasTopLabel
            />
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
        <Box sx={{ flex: 1 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CustomDatePicker
              width='100%'
              label={
                paymentMode === PaymentMode.CHEQUE ||
                paymentMode === PaymentMode.DEMAND_DRAFT
                  ? 'Date of Issue'
                  : 'Payment Date'
              }
              value={paymentDate}
              onChange={(newValue: Dayjs | null) => setPaymentDate(newValue)}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ flex: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CustomDatePicker
              label='Deposit Upto Date'
              value={depositUptoDate}
              onChange={(newValue: Dayjs | null) =>
                setDepositUptoDate(newValue)
              }
            />
          </LocalizationProvider>
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <TextInputWithIcon
          sx={{
            '& .css-1crf3f2-MuiInputBase-root-MuiOutlinedInput-root': {
              height: 'auto !important',
              padding: 0
            }
          }}
          label='Remarks'
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          fullWidth
          rows={3}
          hasTopLabel
          required={false}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <DropdownSelect
          label='Payment Status'
          value={paymentStatus}
          options={paymentStatusOptions}
          onChange={(e) =>
            setPaymentStatus(e.target.value as TransactionStatus)
          }
          name='paymentStatus'
          fullWidth
          hasTopLabel
          disabled
        />
      </Box>
    </DialogModal>
  );
};

export default AddPaymentDialog;
